import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';

interface SurveyQuestion {
  id: string;
  questionText: string;
  questionType: string;
  options?: string[];
  required: boolean;
}

interface SurveyPayload {
  survey: {
    id: string;
    title: string;
    introText?: string;
    questions: SurveyQuestion[];
  };
  client: { id: string; fullLegalName: string } | null;
  alreadySubmitted: boolean;
}

export default function SurveyResponse() {
  const { token } = useParams();
  const [data, setData] = useState<SurveyPayload | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<{ id: string; submittedAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    loadSurvey(token);
  }, [token]);

  const loadSurvey = async (value: string) => {
    try {
      const response = await api.get(`/api/surveys/token/${value}`);
      setData(response.data);
    } catch (err) {
      setError('This survey link is invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    try {
      const response = await api.post('/api/surveys/respond', { token, answers });
      setReceipt({ id: response.data.receiptId, submittedAt: response.data.submittedAt });
    } catch (err) {
      setError('Unable to submit survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading survey...</div>;
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-800">{error}</div>;
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center">Survey not found.</div>;
  }

  if (receipt) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="card max-w-lg w-full text-center">
          <h2 className="text-2xl font-bold mb-2">Survey Submitted</h2>
          <p className="text-dark-gray mb-4">Thank you for completing the survey.</p>
          <div className="bg-light-gray rounded p-4 text-sm">
            Receipt ID: <strong>{receipt.id}</strong>
            <div>Submitted: {new Date(receipt.submittedAt).toLocaleString()}</div>
          </div>
        </div>
      </div>
    );
  }

  if (data.alreadySubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="card max-w-lg w-full text-center">
          <h2 className="text-2xl font-bold mb-2">Already Submitted</h2>
          <p className="text-dark-gray">This survey was already completed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F3F4F6' }}>
      <div className="card max-w-2xl w-full">
        <h2 className="text-2xl font-bold mb-1">{data.survey.title}</h2>
        {data.client && <div className="text-sm text-dark-gray mb-4">For: {data.client.fullLegalName}</div>}
        {data.survey.introText && <p className="text-dark-gray mb-6">{data.survey.introText}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          {data.survey.questions.map((q) => (
            <div key={q.id}>
              <label className="block text-sm font-medium mb-2">
                {q.questionText} {q.required && '*'}
              </label>
              {q.questionType === 'YES_NO' && (
                <select
                  required={q.required}
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                >
                  <option value="">Select</option>
                  <option value="YES">Yes</option>
                  <option value="NO">No</option>
                </select>
              )}
              {q.questionType === 'SCALE' && (
                <select
                  required={q.required}
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                >
                  <option value="">Select</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              )}
              {q.questionType === 'MULTIPLE_CHOICE' && (
                <select
                  required={q.required}
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                >
                  <option value="">Select</option>
                  {(q.options || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
              {(!q.questionType || q.questionType === 'SHORT_TEXT') && (
                <input
                  type="text"
                  required={q.required}
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              )}
            </div>
          ))}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
